import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Button,
    Col,
    FormCheck,
    Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade } from '../../../hook';
import {
    SchoolRank,
    StudentWithGradeNo,
    UserInfo,
} from '../../../model';
import apis from '../../../apis';
import {
    SheetHeaderDisplay,
} from '../../../utils';
import { toast } from 'react-toastify';

const tableHeader: (
    SheetHeaderDisplay<StudentWithGradeNo> &
    HeaderDisplayKeysWithType<StudentWithGradeNo>
)[] = [
        {
            display: '統編',
            property: 'pid',
        },
        {
            display: '年級',
            property: 'grade',
        },
        {
            display: '班級',
            property: 'className',
        },
        {
            display: '學生',
            property: 'name',
        },
        {
            display: '座號',
            property: 'seat',
        },
        {
            display: '性別',
            property: 'sex',
            onRender: (v) =>
                v === '1' ? '男' : '女',
            onSheetRender: (v) =>
                v === '1' ? '男' : '女',
        },
    ];

const mapState = (
    app: ApplicationState
) => ({
    ...app.auth,
});

const mapDispatch =
    ErrorDispatches;

const connector = connect(
    mapState,
    mapDispatch
);

type Props =
    ConnectedProps<typeof connector>;

const healthExaminationRecord:
    FunctionComponent<Props> = ({
        user,
        catchErrorForModal,
    }) => {
        const {
            currentSemester = '',
            semesters = {},
            currentRank,
        } = user || {};

        const [
            currentSem,
        ] =
            useState(
                currentSemester
            );

        const {
            year = -1,
            sem = -1,
        } =
            semesters[
            currentSem
            ];

        const yearSem = {
            year,
            sem,
        };

        const [
            students,
            setStudents,
        ] =
            useState<
                StudentWithGradeNo[]
            >([]);

        const {
            selectedClassGrade:
            classGrade,
            element:
            classGradeElement,
        } = useClassGrade({
            yearSem,
        });

        const healthGradeOptionsMap:
            Record<
                number,
                string[]
            > = {
            [SchoolRank.Primary]:
                ['國小'],
            [SchoolRank.Junior]:
                ['國中'],
            [SchoolRank.Senior]:
                ['高中'],
            [SchoolRank.Senior4]:
                ['高中'],
            [SchoolRank.PrimaryJunior]:
                [
                    '國小',
                    '國中',
                ],
            [SchoolRank.JuniorSenior]:
                [
                    '國中',
                    '高中',
                ],
        };

        const healthGradeOptions =
            healthGradeOptionsMap[
            currentRank
            ] ?? ['國小'];

        const [
            healthGrade,
            setHealthGrade,
        ] =
            useState<string>(
                healthGradeOptions[
                0
                ]
            );

        useEffect(() => {
            if (
                !classGrade?.id
            ) {
                setStudents(
                    []
                );
                return;
            }

            toast
                .promise(
                    apis
                        .GetHealthCardStudent(
                            classGrade.id
                        )
                        .then(
                            (r) => {
                                setStudents(
                                    r
                                );
                            }
                        ),
                    {
                        pending:
                            '學生資料載入中......',
                        success:
                            '學生資料載入成功！',
                        error:
                            '學生資料載入失敗！請查看錯誤資訊。',
                    }
                )
                .catch(
                    catchErrorForModal
                );
        }, [
            classGrade?.id,
        ]);

        const handleBatchPrint =
            () => {
                if (
                    !students.length
                ) {
                    return;
                }

                const pids = students.map((s) => s.pid);
                toast
                    .promise(
                        apis.downloadWHHealthCards(
                            healthGrade,
                            pids
                        ),
                        {
                            pending:
                                '班級健康檢查紀錄卡產生中...',
                            success:
                                '班級健康檢查紀錄卡產生成功！',
                            error:
                                '班級健康檢查紀錄卡產生失敗！請查看錯誤資訊。',
                        }
                    )
                    .then(
                        (blob) => {
                            const pdfBlob =
                                blob instanceof
                                    Blob
                                    ? blob.slice(
                                        0,
                                        blob.size,
                                        'application/pdf'
                                    )
                                    : new Blob(
                                        [
                                            blob,
                                        ],
                                        {
                                            type:
                                                'application/pdf',
                                        }
                                    );

                            const pdfUrl =
                                window.URL.createObjectURL(
                                    pdfBlob
                                );

                            const pdfWindow =
                                window.open(
                                    pdfUrl,
                                    '_blank'
                                );

                            if (
                                !pdfWindow
                            ) {
                                toast.error(
                                    '瀏覽器阻擋了 PDF 視窗，請允許彈出式視窗。'
                                );

                                window.URL.revokeObjectURL(
                                    pdfUrl
                                );

                                return;
                            }

                            setTimeout(
                                () => {
                                    window.URL.revokeObjectURL(
                                        pdfUrl
                                    );
                                },
                                60000
                            );
                        }
                    )
                    .catch(
                        catchErrorForModal
                    );
            };

        return (
            <AuthedLayout>
                <Row className="align-items-start mb-3">
                    <Col
                        xs={12}
                        md="auto"
                        className="mr-4 mb-2"
                    >
                        {
                            classGradeElement
                        }
                    </Col>

                    <Col
                        xs={12}
                        md="auto"
                        className="mr-4 mb-2"
                    >
                        <div className="font-weight-bold mb-1">
                            選擇紀錄卡樣式：
                        </div>

                        <div className="d-flex flex-column">
                            {healthGradeOptions.map(
                                (
                                    grade
                                ) => (
                                    <FormCheck
                                        key={
                                            grade
                                        }
                                        className="mb-1"
                                        value={
                                            grade
                                        }
                                        type="radio"
                                        label={
                                            grade
                                        }
                                        name="health-grade"
                                        checked={
                                            healthGrade ===
                                            grade
                                        }
                                        onChange={() =>
                                            setHealthGrade(
                                                grade
                                            )
                                        }
                                    />
                                )
                            )}
                        </div>
                    </Col>

                    <Col
                        xs={12}
                        md
                        className="mb-2"
                    >
                        <div className="font-weight-bold">
                            紀錄卡樣式差異：
                        </div>

                        <div className="mb-2">
                            不同學制檢查的項目也有不同，請依學生的年級做選擇。
                        </div>

                        <div className="d-flex flex-column">
                            <div>
                                1年級~6年級：國小
                            </div>
                            <div>
                                7年級~9年級：國中
                            </div>
                            <div>
                                10年級以上：高中
                            </div>
                        </div>
                    </Col>

                    <Col
                        xs={12}
                        md="auto"
                        className="mb-2"
                    >
                        <Button
                            disabled={
                                !students.length
                            }
                            variant="success"
                            className="text-dark"
                            onClick={
                                handleBatchPrint
                            }
                        >
                            列印班級健康檢查紀錄卡
                        </Button>
                    </Col>
                </Row>

                <hr />

                <Row>
                    <EditableTableDiv
                        headers={
                            tableHeader
                        }
                        values={
                            students
                        }
                    />
                </Row>
            </AuthedLayout>
        );
    };

export const HealthExaminationRecord =
    connector(
        healthExaminationRecord
    );